/**
 * Componente SubscriptionStatus
 * Muestra el estado actual de la suscripción del usuario con información detallada
 * 
 * Mejoras implementadas:
 * - Información completa del plan (precio, frecuencia, período de prueba)
 * - Fechas importantes con formato localizado
 * - Información de pago de Mercado Pago
 * - Historial de pagos con estados y montos
 * - Configuración y errores de pago
 * - Soporte para múltiples monedas y métodos de pago
 * - UI organizada por secciones con colores distintivos
 */

import React from 'react';
import { 
  SUBSCRIPTION_STATUS_DISPLAY, 
  SUBSCRIPTION_STATUS_COLORS,
  PAYMENT_STATUS_DISPLAY,
  PAYMENT_STATUS_COLORS,
  SUPPORTED_CURRENCIES,
  SUBSCRIPTION_FREQUENCY_DISPLAY
} from '../../constants/subscriptions';

/**
 * Componente para mostrar estado de suscripción
 * @param {Object} subscription - Datos de la suscripción
 * @param {boolean} showDetails - Mostrar detalles adicionales
 * @param {string} size - Tamaño del componente ('sm', 'md', 'lg')
 * @param {boolean} showPayments - Mostrar historial de pagos
 */
const SubscriptionStatus = ({ 
  subscription, 
  showDetails = false, 
  size = 'md',
  showPayments = false
}) => {
  // Función para formatear moneda
  const formatCurrency = (amount, currency = 'ARS') => {
    const currencyInfo = SUPPORTED_CURRENCIES[currency];
    if (!currencyInfo) return `${currency} ${amount}`;
    return `${currencyInfo.symbol} ${amount.toFixed(currencyInfo.decimalPlaces)}`;
  };

  // Función para formatear fecha
  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(includeTime && { hour: '2-digit', minute: '2-digit' })
    };
    return date.toLocaleDateString('es-ES', options);
  };

  // Función para obtener el método de pago desde datos_pago
  const getPaymentMethod = (datosPago) => {
    if (!datosPago || !datosPago.payment_method_id) return 'No especificado';
    
    const methodMap = {
      'account_money': 'Dinero en cuenta',
      'credit_card': 'Tarjeta de crédito',
      'debit_card': 'Tarjeta de débito',
      'ticket': 'Boleto',
      'bank_transfer': 'Transferencia bancaria'
    };
    
    return methodMap[datosPago.payment_method_id] || datosPago.payment_method_id;
  };
  if (!subscription) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-600">
        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
        <span className="text-sm font-medium">Sin suscripción</span>
      </div>
    );
  }

  const status = subscription.estado;
  const statusDisplay = SUBSCRIPTION_STATUS_DISPLAY[status] || status;
  const statusColors = SUBSCRIPTION_STATUS_COLORS[status] || SUBSCRIPTION_STATUS_COLORS.pending;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSize = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3'
  };

  return (
    <div className="space-y-2">
      {/* Badge principal */}
      <div 
        className={`
          inline-flex items-center gap-2 rounded-full
          ${statusColors.bg} ${statusColors.border} ${statusColors.text}
          ${sizeClasses[size]}
        `}
      >
        <div className={`${iconSize[size]} ${statusColors.text.replace('text', 'bg')} rounded-full`}></div>
        <span className="font-medium">{statusDisplay}</span>
        {statusColors.icon && <span>{statusColors.icon}</span>}
      </div>

      {/* Detalles adicionales */}
      {showDetails && (
        <div className="space-y-3 text-sm text-gray-600">
          {/* Información del Plan */}
          {subscription.planes && (
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <h4 className="font-semibold text-gray-800 mb-2">Información del Plan</h4>
              
              <div className="flex items-center gap-2">
                <span className="font-medium">Plan:</span>
                <span>{subscription.planes.nombre}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Precio:</span>
                <span className="text-green-600 font-semibold">
                  {formatCurrency(subscription.planes.plan_price_promo || subscription.planes.precio, subscription.planes.moneda)}
                </span>
                {subscription.planes.plan_price_promo && (
                  <span className="text-gray-400 line-through text-xs">
                    {formatCurrency(subscription.planes.precio, subscription.planes.moneda)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Frecuencia:</span>
                <span>{SUBSCRIPTION_FREQUENCY_DISPLAY[subscription.planes.frecuencia] || subscription.planes.frecuencia}</span>
              </div>

              {subscription.planes.trial_days > 0 && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Período de prueba:</span>
                  <span className="text-blue-600">{subscription.planes.trial_days} días</span>
                </div>
              )}
            </div>
          )}

          {/* Fechas importantes */}
          <div className="bg-blue-50 p-3 rounded-lg space-y-2">
            <h4 className="font-semibold text-gray-800 mb-2">Fechas Importantes</h4>
            
            {/* Fecha de inicio */}
            {subscription.fecha_inicio && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Inicio:</span>
                <span>{formatDate(subscription.fecha_inicio)}</span>
              </div>
            )}

            {/* Próximo pago */}
            {subscription.proximo_pago && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Próximo pago:</span>
                <span className="text-orange-600 font-semibold">
                  {formatDate(subscription.proximo_pago)}
                </span>
              </div>
            )}

            {/* Fecha de fin */}
            {subscription.fecha_fin && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Fin:</span>
                <span>{formatDate(subscription.fecha_fin)}</span>
              </div>
            )}

            {/* Fecha de cancelación */}
            {subscription.fecha_cancelacion && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Cancelada:</span>
                <span className="text-red-600">{formatDate(subscription.fecha_cancelacion)}</span>
              </div>
            )}

            {/* Fecha de creación */}
            {subscription.creado_en && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Creada:</span>
                <span className="text-xs text-gray-500">{formatDate(subscription.creado_en, true)}</span>
              </div>
            )}
          </div>

          {/* Información de Pago */}
          {subscription.datos_pago && (
            <div className="bg-green-50 p-3 rounded-lg space-y-2">
              <h4 className="font-semibold text-gray-800 mb-2">Información de Pago</h4>
              
              <div className="flex items-center gap-2">
                <span className="font-medium">Monto:</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(subscription.datos_pago.auto_recurring?.transaction_amount || subscription.monto, subscription.moneda)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Método:</span>
                <span>{getPaymentMethod(subscription.datos_pago)}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Estado MP:</span>
                <span className="capitalize">{subscription.datos_pago.status}</span>
              </div>

              {subscription.datos_pago.payer_email && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Email pagador:</span>
                  <span className="text-xs text-gray-600">{subscription.datos_pago.payer_email}</span>
                </div>
              )}

              {subscription.datos_pago.reason && (
                <div className="flex items-start gap-2">
                  <span className="font-medium">Descripción:</span>
                  <span className="text-xs text-gray-600">{subscription.datos_pago.reason}</span>
                </div>
              )}
            </div>
          )}

          {/* Historial de Pagos */}
          {showPayments && subscription.pagos_suscripciones && subscription.pagos_suscripciones.length > 0 && (
            <div className="bg-purple-50 p-3 rounded-lg space-y-2">
              <h4 className="font-semibold text-gray-800 mb-2">Historial de Pagos</h4>
              
              {subscription.pagos_suscripciones.map((pago, index) => {
                const paymentStatusColors = PAYMENT_STATUS_COLORS[pago.mp_status] || PAYMENT_STATUS_COLORS.pending;
                return (
                  <div key={pago.id} className="border-b border-purple-100 pb-2 last:border-b-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">#{pago.id}</span>
                        <span className={`${paymentStatusColors.bg} ${paymentStatusColors.text} px-2 py-1 rounded-full text-xs`}>
                          {PAYMENT_STATUS_DISPLAY[pago.mp_status] || pago.mp_status}
                        </span>
                      </div>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(pago.monto, pago.moneda)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>Fecha: {formatDate(pago.fecha_pago, true)}</span>
                      {pago.fecha_aprobacion && (
                        <span>• Aprobado: {formatDate(pago.fecha_aprobacion, true)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Estado y configuración */}
          <div className="bg-yellow-50 p-3 rounded-lg space-y-2">
            <h4 className="font-semibold text-gray-800 mb-2">Configuración</h4>
            
            {/* Intentos de pago */}
            {subscription.intentos_pago !== undefined && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Intentos de pago:</span>
                <span className={subscription.intentos_pago > 0 ? 'text-orange-600' : 'text-green-600'}>
                  {subscription.intentos_pago}
                </span>
              </div>
            )}

            {/* Último error de pago */}
            {subscription.ultimo_error_pago && (
              <div className="flex items-start gap-2">
                <span className="font-medium">Último error:</span>
                <span className="text-red-600 text-xs">{subscription.ultimo_error_pago}</span>
              </div>
            )}

            {/* Motivo de cancelación */}
            {subscription.motivo_cancelacion && (
              <div className="flex items-start gap-2">
                <span className="font-medium">Motivo cancelación:</span>
                <span className="text-gray-500 text-xs">{subscription.motivo_cancelacion}</span>
              </div>
            )}

            {/* ID de Mercado Pago */}
            {/* {subscription.mp_preapproval_id && (
              <div className="flex items-start gap-2">
                <span className="font-medium">ID Mercado Pago:</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
                  {subscription.mp_preapproval_id}
                </span>
              </div>
            )} */}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;
